import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Quote, ArrowRight } from 'lucide-react';
import { clientReviews } from '../../data/mock';

const truncate = (text, max = 180) => {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '...';
};

const ReviewsTeaserSection = () => {
  const featured = clientReviews.slice(0, 3);

  return (
    <section
      className="py-16 sm:py-20 relative overflow-hidden bg-slate-900"
      data-testid="reviews-teaser-section"
    >
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" aria-hidden="true"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-4">
            <Star className="w-4 h-4 fill-amber-400" aria-hidden="true" />
            What Clients Say
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
            Real Progress, <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Real Clients</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-3" data-testid="reviews-teaser-rating">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" aria-hidden="true" />
              ))}
            </div>
            <span className="text-white text-sm font-semibold">5.0</span>
            <span className="text-slate-400 text-sm">· All {clientReviews.length} reviews are 5-star</span>
          </div>
          <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
            Genuine experiences shared with consent. Individual results vary.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto mb-10">
          {featured.map((review, i) => (
            <Link
              to="/reviews"
              key={review.id}
              className="group bg-slate-800/50 border border-slate-700/60 rounded-2xl p-6 hover:border-slate-600 transition-colors flex flex-col"
              data-testid={`reviews-teaser-card-${i}`}
            >
              <div className="flex items-center gap-0.5 mb-3" aria-label={`${review.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-4 h-4 ${s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <h3 className="text-white font-semibold text-base mb-2 leading-snug">{review.title}</h3>
              <div className="relative flex-1">
                <Quote className="w-5 h-5 text-blue-500/20 absolute -top-1 -left-0.5" aria-hidden="true" />
                <p className="text-slate-400 text-sm leading-relaxed pl-5">{truncate(review.text[0])}</p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-700/60 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {review.name?.charAt(0) || 'C'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate">{review.name}</div>
                    <div className="text-slate-500 text-[11px] truncate">{review.context}</div>
                  </div>
                </div>
                <span className="text-blue-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">Read</span>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-white px-6 py-3 rounded-full font-semibold text-sm sm:text-base transition-all"
            data-testid="reviews-teaser-view-all-btn"
          >
            Read All Client Reviews <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ReviewsTeaserSection;
